/**
 * Core SDK for One Payments - Framework-agnostic payment processing
 *
 * This module provides the main SDK class for integrating One Payments into your application.
 * It handles payment intent creation, payment method selection, payment processing, and status polling.
 *
 * @module @one-payments/core
 * @since 1.0.0
 */

import type { Adapters } from './adapters/types.js';
import type {
  State,
  SDKConfig,
  PaymentIntent,
  PaymentMethodId,
  Environment,
} from './types/state.js';
import type { Event, EventListener, EventListenerMap } from './types/events.js';
import type {
  ApiResponse,
  CreatePaymentIntentResult,
  PaymentDataResult,
  AvailableMethodsResult,
  ConfirmPaymentRequest,
  BanksResult,
  Bank,
} from './types/api.js';
import { isHttpError } from './types/api.js';
import { PAYMENT_STATUS } from './types/state.js';
import { PollingService, type PaymentStatusResponse } from './services/polling.js';

/**
 * Get API base URL from environment configuration
 *
 * Maps environment identifiers to their corresponding API base URLs.
 * Note: 'dev' is supported at runtime for internal use but not exposed in types.
 *
 * @param environment - The environment identifier ('prod' or 'demo')
 * @returns The API base URL for the specified environment
 *
 * @internal
 */
function getBaseUrlFromEnvironment(environment: Environment | string): string {
  switch (environment) {
    case 'prod':
      return 'https://public.prod.one.ooo';
    case 'demo':
      return 'https://public.demo.one.ooo';
    case 'dev':
      return 'https://public.dev.one.ooo';
    default:
      return 'https://public.dev.one.ooo';
  }
}

/**
 * State change subscriber callback function
 *
 * Called whenever the SDK state changes, allowing you to react to payment lifecycle events.
 *
 * @param state - The new state object
 *
 * @example
 * ```typescript
 * const unsubscribe = sdk.subscribe((state) => {
 *   if (state.status === 'succeeded') {
 *     console.log('Payment successful!');
 *   }
 * });
 * ```
 *
 * @public
 */
export type Subscriber = (state: State) => void;

/**
 * PaymentSDK - Main SDK class for One Payments integration
 *
 * The PaymentSDK provides a complete payment processing solution with support for:
 * - Multiple payment methods (cards, PayNow, etc.)
 * - 3D Secure authentication
 * - QR code-based payments
 * - Real-time payment status updates via polling
 * - Type-safe state management
 * - Event-driven architecture
 *
 * The SDK is framework-agnostic and uses an adapter pattern to work across different
 * platforms (web, React Native, etc.) without coupling to browser or native APIs.
 *
 * @example
 * ```typescript
 * import { PaymentSDK } from '@one-payments/core';
 * import { createWebAdapters } from '@one-payments/adapters';
 *
 * const sdk = new PaymentSDK(
 *   {
 *     apiKey: 'your-api-key',
 *     secretKey: 'your-secret-key',
 *     environment: 'demo',
 *   },
 *   createWebAdapters()
 * );
 *
 * // Initialize payment intent
 * const paymentIntent = await sdk.initialize({
 *   amount: 5000,
 *   currency: 'USD',
 *   orderId: 'ORDER-123',
 * });
 *
 * // Submit payment
 * await sdk.submitPayment('card', {
 *   firstName: 'John',
 *   lastName: 'Doe',
 *   email: 'john@example.com',
 *   cardNumber: '4242424242424242',
 *   expiryMonth: '12',
 *   expiryYear: '2025',
 *   cvv: '123',
 * });
 * ```
 *
 * @public
 * @since 1.0.0
 */
export class PaymentSDK {
  private state: State = { status: PAYMENT_STATUS.IDLE };
  private readonly subscribers = new Set<Subscriber>();
  private readonly eventListeners: EventListenerMap = {};
  private readonly adapters: Adapters;
  private readonly config: SDKConfig;
  private paymentToken: string | null = null;
  private paymentIntentId: string | null = null;
  private pollingService: PollingService | null = null;
  private ignore3DSInPolling = false; // Flag to ignore nextActionUrl after 3DS completes
  private ignoreQRInPolling = false; // Flag to ignore qrCode after user scans it

  constructor(config: SDKConfig, adapters: Adapters) {
    this.config = config;
    this.adapters = adapters;
  }

  /**
   * Get the API base URL - uses apiBaseUrl if provided, otherwise derives from environment
   */
  private getApiBaseUrl(): string {
    return this.config.apiBaseUrl ?? getBaseUrlFromEnvironment(this.config.environment);
  }

  /**
   * Get the current payment state
   *
   * Returns an immutable snapshot of the current SDK state. The state follows a discriminated
   * union pattern, where the `status` field determines which additional fields are available.
   *
   * @returns The current state object (readonly)
   *
   * @example
   * ```typescript
   * const state = sdk.getState();
   *
   * if (state.status === 'ready') {
   *   console.log('Available methods:', state.availableMethods);
   *   console.log('Payment intent:', state.paymentIntent);
   * } else if (state.status === 'failed') {
   *   console.error('Error:', state.error);
   * }
   * ```
   *
   * @public
   */
  public getState(): Readonly<State> {
    return this.state;
  }

  /**
   * Subscribe to state changes
   *
   * Registers a callback function that will be called immediately with the current state,
   * and then again whenever the state changes. This is useful for reactive UI updates.
   *
   * @param subscriber - Callback function to be called with state updates
   * @returns Unsubscribe function - call this to stop receiving updates
   *
   * @example
   * ```typescript
   * const unsubscribe = sdk.subscribe((state) => {
   *   switch (state.status) {
   *     case 'ready':
   *       console.log('Ready to accept payment');
   *       break;
   *     case 'processing':
   *       console.log('Processing payment...');
   *       break;
   *     case 'succeeded':
   *       console.log('Payment succeeded!');
   *       break;
   *     case 'failed':
   *       console.error('Payment failed:', state.error.message);
   *       break;
   *   }
   * });
   *
   * // Later, when you want to stop listening:
   * unsubscribe();
   * ```
   *
   * @public
   */
  public subscribe(subscriber: Subscriber): () => void {
    this.subscribers.add(subscriber);
    // Immediately call with current state
    subscriber(this.state);

    return (): void => {
      this.subscribers.delete(subscriber);
    };
  }

  /**
   * Add an event listener for specific payment events
   *
   * Allows you to listen to specific payment lifecycle events with full type safety.
   * Unlike `subscribe`, which receives all state changes, this method lets you listen
   * to specific events like payment initialization, success, failure, etc.
   *
   * @param eventType - The type of event to listen for
   * @param listener - Callback function to be called when the event occurs
   * @returns Unsubscribe function - call this to remove the listener
   *
   * @example
   * ```typescript
   * // Listen for successful payments
   * const removeListener = sdk.on('payment_succeeded', (event) => {
   *   console.log('Payment succeeded!', event.payload.paymentIntent);
   *   console.log('Method used:', event.payload.methodId);
   * });
   *
   * // Listen for QR codes
   * sdk.on('qr_code_received', (event) => {
   *   displayQRCode(event.payload.qrCode);
   * });
   *
   * // Listen for required actions (3DS)
   * sdk.on('payment_requires_action', (event) => {
   *   openAuthenticationWindow(event.payload.actionUrl);
   * });
   *
   * // Later, remove the listener:
   * removeListener();
   * ```
   *
   * @public
   */
  public on<T extends Event['type']>(
    eventType: T,
    listener: EventListener<Extract<Event, { type: T }>>,
  ): () => void {
    if (!this.eventListeners[eventType]) {
      this.eventListeners[eventType] = [];
    }
    // Type assertion needed due to TypeScript limitation with discriminated unions
    const listeners = this.eventListeners[eventType] as (typeof listener)[];
    listeners.push(listener);

    return (): void => {
      const index = listeners.indexOf(listener);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    };
  }

  /**
   * Initialize a new payment intent
   *
   * Creates a payment intent on the server and fetches available payment methods.
   * This must be called before attempting to submit a payment. The SDK state will
   * transition from 'idle' → 'initializing' → 'ready' (or 'failed' on error).
   *
   * @param params - Payment initialization parameters
   * @param params.amount - Payment amount in smallest currency unit (e.g., cents for USD)
   * @param params.currency - ISO 4217 currency code (e.g., 'USD', 'SGD', 'EUR')
   * @param params.orderId - Your unique order/transaction identifier
   * @param params.notes - Optional notes or description for the payment
   * @param params.metadata - Optional metadata object with custom key-value pairs
   * @returns Promise that resolves with the created payment intent
   * @throws {Error} If initialization fails (network error, invalid credentials, etc.)
   *
   * @example
   * ```typescript
   * try {
   *   const paymentIntent = await sdk.initialize({
   *     amount: 5000, // $50.00
   *     currency: 'USD',
   *     orderId: 'ORDER-123',
   *     notes: 'Premium subscription',
   *     metadata: {
   *       userId: '12345',
   *       subscriptionId: 'sub_abc',
   *     },
   *   });
   *
   *   console.log('Payment intent created:', paymentIntent.id);
   *   console.log('Amount with fees:', paymentIntent.amount);
   * } catch (error) {
   *   console.error('Failed to initialize:', error);
   * }
   * ```
   *
   * @public
   */
  public async initialize(params: {
    amount: number;
    currency: string;
    orderId: string;
    notes?: string;
    metadata?: Record<string, string | number | boolean>;
  }): Promise<PaymentIntent> {
    this.setState({ status: PAYMENT_STATUS.INITIALIZING });
    this.emit({
      type: 'initialize',
      payload: params,
    });

    try {
      // Prepare request body according to API spec
      const requestBody = {
        amount: params.amount,
        currency: params.currency,
        notes: params.notes,
        orderId: params.orderId,
      };

      // Create HMAC signature of request body
      const bodyString = JSON.stringify(requestBody);
      const signature = await this.adapters.crypto.hmacSHA256(this.config.secretKey, bodyString);

      // Call correct endpoint with proper headers
      const response = await this.adapters.http.post<ApiResponse<CreatePaymentIntentResult>>(
        `${this.getApiBaseUrl()}/payment-intent/create`,
        requestBody,
        {
          'x-api-key': this.config.apiKey,
          'x-signature': signature,
        },
      );

      // Extract token from redirectUrl
      const redirectUrl = new URL(response.result.redirectUrl);
      const token = redirectUrl.searchParams.get('token');

      if (!token) {
        throw new Error('No token found in redirectUrl');
      }

      // Store token and payment intent ID for later use
      this.paymentToken = token;
      this.paymentIntentId = response.result.paymentIntentId;

      // Call get-payment-data to fetch fees and payment details
      const paymentDataResponse = await this.adapters.http.get<ApiResponse<PaymentDataResult>>(
        `${this.getApiBaseUrl()}/payment-intent/get-payment-data`,
        {
          'x-token': this.paymentToken,
        },
      );

      // Call available-methods to fetch enabled payment methods (requires currency parameter)
      const availableMethodsUrl = `${this.getApiBaseUrl()}/payment-intent/available-methods?currency=${params.currency}`;
      console.log('[SDK] Calling available-methods with URL:', availableMethodsUrl);
      console.log('[SDK] params.currency:', params.currency);
      const availableMethodsResponse = await this.adapters.http.get<
        ApiResponse<AvailableMethodsResult>
      >(availableMethodsUrl, {
        'x-token': this.paymentToken,
      });

      // Create PaymentIntent object for state with payment data
      const paymentIntent: PaymentIntent = {
        id: this.paymentIntentId,
        amount: paymentDataResponse.result.customerAmount || params.amount,
        currency: paymentDataResponse.result.currency || params.currency,
        status: PAYMENT_STATUS.READY,
        ...(params.metadata && { metadata: params.metadata }),
      };

      // Map API payment methods to SDK payment method configs
      // API can return types like "Card Payment", "Bank Transfer", etc.
      // We also check capabilities array for more accurate mapping
      // One API method can have multiple capabilities (e.g., Bank Transfer with both PayNow and Prompt Pay)
      const availableMethods: { id: PaymentMethodId; enabled: boolean; priority: number }[] = [];
      let priorityCounter = 0;

      // Always add Card payment method first (regardless of API response)
      availableMethods.push({
        id: 'card',
        enabled: true,
        priority: priorityCounter++,
      });

      availableMethodsResponse.result.paymentMethods.forEach((method) => {
        const typeLower = method.type.toLowerCase();
        const capabilities = method.capabilities || [];

        // Skip card - already added above
        if (typeLower.includes('card')) {
          return;
        }

        // Check for PayNow - can be "Bank Transfer" type with "payments_pay_now" capability
        if (typeLower.includes('paynow') || capabilities.includes('payments_pay_now')) {
          availableMethods.push({
            id: 'paynow',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for Prompt Pay - Thailand's payment system with "payments_prompt_pay" capability
        if (
          typeLower.includes('promptpay') ||
          typeLower.includes('prompt pay') ||
          capabilities.includes('payments_prompt_pay')
        ) {
          availableMethods.push({
            id: 'promptpay',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for DuitNow - Malaysia's payment system with "payments_duit_now" capability
        if (
          typeLower.includes('duitnow') ||
          typeLower.includes('duit now') ||
          capabilities.includes('payments_duit_now')
        ) {
          availableMethods.push({
            id: 'duitnow',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for GoPay - Indonesia's e-wallet with "payments_go_pay" capability
        if (
          typeLower.includes('gopay') ||
          typeLower.includes('go pay') ||
          capabilities.includes('payments_go_pay')
        ) {
          availableMethods.push({
            id: 'gopay',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for Boost - Malaysia's e-wallet with "payments_boost" capability
        if (typeLower.includes('boost') || capabilities.includes('payments_boost')) {
          availableMethods.push({
            id: 'boost',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for ShopeePay - Southeast Asia's e-wallet with "payments_shopee_pay" capability
        if (
          typeLower.includes('shopeepay') ||
          typeLower.includes('shopee pay') ||
          typeLower.includes('shopee_pay') ||
          capabilities.includes('payments_shopee_pay')
        ) {
          availableMethods.push({
            id: 'shopeepay',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for Atom - Singapore/Malaysia payment method with "payments_atome" capability
        if (
          typeLower.includes('atom') ||
          typeLower.includes('atome') ||
          capabilities.includes('payments_atome')
        ) {
          availableMethods.push({
            id: 'atom',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for Dana - Indonesia's e-wallet with "payments_dana" capability
        if (typeLower.includes('dana') || capabilities.includes('payments_dana')) {
          availableMethods.push({
            id: 'dana',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for Touch 'n Go - Malaysia's e-wallet with "payments_tng" capability
        if (
          typeLower.includes('tng') ||
          typeLower.includes('touch') ||
          capabilities.includes('payments_tng')
        ) {
          availableMethods.push({
            id: 'tng',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for Alipay CN - China's payment platform with "payments_alipaycn" capability
        if (
          typeLower.includes('alipaycn') ||
          typeLower.includes('alipay cn') ||
          typeLower.includes('alipay_cn') ||
          capabilities.includes('payments_alipaycn')
        ) {
          availableMethods.push({
            id: 'alipaycn',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for Alipay HK - Hong Kong's payment platform with "payments_alipayhk" capability
        if (
          typeLower.includes('alipayhk') ||
          typeLower.includes('alipay hk') ||
          typeLower.includes('alipay_hk') ||
          capabilities.includes('payments_alipayhk')
        ) {
          availableMethods.push({
            id: 'alipayhk',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for GCash - Philippines' payment platform with "payments_gcash" capability
        if (
          typeLower.includes('gcash') ||
          typeLower.includes('g cash') ||
          capabilities.includes('payments_gcash')
        ) {
          availableMethods.push({
            id: 'gcash',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for Konbini - Japan's convenience store payment with "payments_konbini" capability
        if (typeLower.includes('konbini') || capabilities.includes('payments_konbini')) {
          availableMethods.push({
            id: 'konbini',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for Pay-Easy - Japan's bank transfer/ATM payment with "payments_payeasy" capability
        if (
          typeLower.includes('payeasy') ||
          typeLower.includes('pay-easy') ||
          typeLower.includes('pay easy') ||
          capabilities.includes('payments_payeasy')
        ) {
          availableMethods.push({
            id: 'payeasy',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for GrabPay SG - Singapore's GrabPay with "payments_grabpay_sg" capability
        if (
          typeLower.includes('grabpay') ||
          typeLower.includes('grab pay') ||
          capabilities.includes('payments_grabpay_sg')
        ) {
          availableMethods.push({
            id: 'grabpay_sg',
            enabled: true,
            priority: priorityCounter++,
          });
        }

        // Check for FPX - Malaysia's bank transfer with "payments_fpx" capability
        if (typeLower.includes('fpx') || capabilities.includes('payments_fpx')) {
          availableMethods.push({
            id: 'fpx',
            enabled: true,
            priority: priorityCounter++,
          });
        }
      });

      this.setState({
        status: PAYMENT_STATUS.READY,
        paymentIntent,
        availableMethods,
      });

      this.emit({
        type: 'initialized',
        payload: {
          paymentIntent,
          paymentData: paymentDataResponse.result,
        },
      });

      return paymentIntent;
    } catch (error) {
      const paymentError = {
        code: 'INITIALIZATION_FAILED',
        message: error instanceof Error ? error.message : 'Unknown error',
        timestamp: this.adapters.timer.now(),
      };

      this.setState({
        status: PAYMENT_STATUS.FAILED,
        error: paymentError,
      });

      this.emit({
        type: 'payment_failed',
        payload: { error: paymentError },
      });

      throw error;
    }
  }

  /**
   * Get available banks for a specific payment method
   *
   * Fetches the list of banks that support the specified payment method.
   * This is primarily used for FPX payments in Malaysia where the user
   * needs to select their bank before proceeding with the payment.
   *
   * @param paymentMethodType - The payment method type to get banks for (e.g., 'fpx')
   * @returns Promise that resolves with an array of available banks
   * @throws {Error} If SDK is not initialized or the request fails
   *
   * @example
   * ```typescript
   * const banks = await sdk.getBanks('fpx');
   * console.log('Available banks:', banks);
   * // [{ bankName: 'maybank', displayName: 'Maybank', logoUrl: '...' }, ...]
   * ```
   *
   * @public
   */
  public async getBanks(paymentMethodType: string): Promise<Bank[]> {
    if (!this.paymentToken) {
      throw new Error('Cannot get banks: SDK not initialized');
    }

    try {
      const response = await this.adapters.http.get<ApiResponse<BanksResult>>(
        `${this.getApiBaseUrl()}/payment-intent/banks?paymentMethodType=${encodeURIComponent(paymentMethodType)}`,
        {
          'x-token': this.paymentToken,
        },
      );

      return response.result.banks || [];
    } catch (error) {
      console.error('Error fetching banks:', error);
      return [];
    }
  }

  /**
   * Submit a payment with the selected payment method
   *
   * Processes a payment using the specified payment method and customer details.
   * The SDK must be in 'ready' state before calling this method. After submission,
   * the SDK automatically polls for payment status updates.
   *
   * For card payments, the payment may require 3D Secure authentication, which will
   * trigger a 'payment_requires_action' event with an authentication URL.
   *
   * For PayNow/QR payments, a QR code will be provided via 'qr_code_received' event
   * that the customer needs to scan with their mobile banking app.
   *
   * @param methodId - The payment method to use ('card' or 'paynow')
   * @param paymentData - Payment details object containing customer and payment information
   * @param paymentData.firstName - Customer's first name
   * @param paymentData.lastName - Customer's last name
   * @param paymentData.email - Customer's email address
   * @param paymentData.cardNumber - Card number (for card payments)
   * @param paymentData.expiryMonth - Card expiry month (for card payments)
   * @param paymentData.expiryYear - Card expiry year (for card payments)
   * @param paymentData.cvv - Card CVV code (for card payments)
   * @returns Promise that resolves with the payment intent
   * @throws {Error} If SDK is not in ready state, no payment token available, or submission fails
   *
   * @example
   * ```typescript
   * // Card payment
   * try {
   *   await sdk.submitPayment('card', {
   *     firstName: 'John',
   *     lastName: 'Doe',
   *     email: 'john@example.com',
   *     cardNumber: '4242424242424242',
   *     expiryMonth: '12',
   *     expiryYear: '2025',
   *     cvv: '123',
   *   });
   * } catch (error) {
   *   console.error('Payment failed:', error);
   * }
   *
   * // PayNow payment
   * await sdk.submitPayment('paynow', {
   *   firstName: 'John',
   *   lastName: 'Doe',
   *   email: 'john@example.com',
   * });
   * ```
   *
   * @public
   */
  public async submitPayment(
    methodId: PaymentMethodId,
    paymentData: Record<string, unknown>,
  ): Promise<PaymentIntent> {
    const currentState = this.state;

    if (currentState.status !== 'ready') {
      throw new Error('Cannot submit payment: SDK not in ready state');
    }

    if (!this.paymentToken) {
      throw new Error('Cannot submit payment: No payment token available');
    }

    // Reset ignore flags for new payment attempt
    this.ignore3DSInPolling = false;
    this.ignoreQRInPolling = false;

    this.setState({
      status: PAYMENT_STATUS.PROCESSING,
      paymentIntent: currentState.paymentIntent,
      selectedMethod: methodId,
      availableMethods: currentState.availableMethods,
    });

    this.emit({
      type: 'payment_processing',
      payload: {
        paymentIntent: currentState.paymentIntent,
        methodId,
      },
    });

    try {
      // Build correct API payload structure
      // Map SDK method IDs to API type values
      type NonCardPaymentType =
        | 'qr'
        | 'prompt_pay'
        | 'duit_now'
        | 'go_pay'
        | 'boost'
        | 'shopee_pay'
        | 'atome'
        | 'dana'
        | 'tng'
        | 'alipaycn'
        | 'alipayhk'
        | 'gcash'
        | 'konbini'
        | 'payeasy'
        | 'grabpay_sg'
        | 'fpx';

      const getPaymentMethodType = (id: PaymentMethodId): NonCardPaymentType => {
        switch (id) {
          case 'paynow':
            return 'qr';
          case 'promptpay':
            return 'prompt_pay';
          case 'duitnow':
            return 'duit_now';
          case 'gopay':
            return 'go_pay';
          case 'boost':
            return 'boost';
          case 'shopeepay':
            return 'shopee_pay';
          case 'atom':
            return 'atome';
          case 'dana':
            return 'dana';
          case 'tng':
            return 'tng';
          case 'alipaycn':
            return 'alipaycn';
          case 'alipayhk':
            return 'alipayhk';
          case 'gcash':
            return 'gcash';
          case 'konbini':
            return 'konbini';
          case 'payeasy':
            return 'payeasy';
          case 'grabpay_sg':
            return 'grabpay_sg';
          case 'fpx':
            return 'fpx';
          default:
            return 'qr';
        }
      };

      // Format phone number for Japanese payment methods (Konbini, Pay-Easy)
      // Convert +81 prefix to 0 prefix for Japanese API compatibility
      const formatPhoneNumber = (phone: string, method: PaymentMethodId): string => {
        const isJapaneseMethod = method === 'konbini' || method === 'payeasy';
        if (isJapaneseMethod && phone.startsWith('+81')) {
          return '0' + phone.slice(3).replace(/\s/g, ''); // +81 90 1234 5678 → 09012345678
        }
        return phone;
      };

      // Format names for Pay-Easy: combined length must be max 9 characters, uppercase
      const formatNamesForPayEasy = (
        first: string,
        last: string,
      ): { firstName: string; lastName: string } => {
        const maxCombinedLength = 9;
        let firstName = first.toUpperCase();
        let lastName = last.toUpperCase();

        const totalLength = firstName.length + lastName.length;
        if (totalLength > maxCombinedLength) {
          // Distribute characters evenly, prioritizing firstName slightly
          const halfMax = Math.ceil(maxCombinedLength / 2);
          firstName = firstName.slice(0, halfMax);
          lastName = lastName.slice(0, maxCombinedLength - firstName.length);
        }

        return { firstName, lastName };
      };

      // Get formatted names:
      // - ALL payment methods: convert to UPPERCASE
      // - Pay-easy: additional truncation (max 9 chars combined)
      let firstName = String(paymentData['firstName']);
      let lastName = String(paymentData['lastName']);

      if (methodId === 'payeasy') {
        // Pay-easy: uppercase + truncation
        const formatted = formatNamesForPayEasy(firstName, lastName);
        firstName = formatted.firstName;
        lastName = formatted.lastName;
      } else {
        // All other methods: just uppercase
        firstName = firstName.toUpperCase();
        lastName = lastName.toUpperCase();
      }

      // Get bankName if provided (for FPX and similar payment methods)
      const bankName = paymentData['bankName'] ? String(paymentData['bankName']) : undefined;

      const confirmPayload: ConfirmPaymentRequest = {
        firstName,
        lastName,
        email: String(paymentData['email']),
        phoneNumber: formatPhoneNumber(String(paymentData['phoneNumber']), methodId),
        paymentMethod:
          methodId === 'card'
            ? {
                type: 'card' as const,
                card: {
                  number: String(paymentData['cardNumber']),
                  expiryMonth: String(paymentData['expiryMonth']),
                  expiryYear: String(paymentData['expiryYear']),
                  cvc: String(paymentData['cvv']),
                },
              }
            : { type: getPaymentMethodType(methodId) },
        ...(bankName && { bankName }),
      };

      // Send /confirm request (don't wait for status in response)
      await this.adapters.http.patch<PaymentIntent>(
        `${this.getApiBaseUrl()}/payment-intent/confirm`,
        confirmPayload,
        {
          'x-token': this.paymentToken,
        },
      );

      // ALWAYS start polling immediately after /confirm
      // Don't handle status from /confirm response
      this.startPolling();

      // Return a placeholder intent (real data comes from polling)
      return {
        id: this.paymentIntentId || '',
        amount: currentState.paymentIntent.amount,
        currency: currentState.paymentIntent.currency,
        status: PAYMENT_STATUS.PROCESSING,
      };
    } catch (error) {
      // Try to extract error message from API response
      let errorMessage = 'Unknown error';
      let errorCode = 'PAYMENT_FAILED';

      if (error instanceof Error) {
        // First try to parse as API error response
        try {
          // Check if error has response data (common pattern in HTTP clients)
          if (isHttpError(error)) {
            const responseData = error.response?.data;
            const directData = error.data;

            if (responseData) {
              errorMessage = responseData.message || responseData.error?.message || error.message;
              errorCode = responseData.code || responseData.error?.code || errorCode;
            } else if (directData) {
              errorMessage = directData.message || directData.error?.message || error.message;
              errorCode = directData.code || directData.error?.code || errorCode;
            } else {
              // Fallback to error message
              errorMessage = error.message;
            }
          } else {
            // Standard Error without HTTP response data
            errorMessage = error.message;
          }
        } catch {
          // If parsing fails, use original error message
          errorMessage = error.message;
        }
      }

      const paymentError = {
        code: errorCode,
        message: errorMessage,
        timestamp: this.adapters.timer.now(),
      };

      this.setState({
        status: PAYMENT_STATUS.FAILED,
        error: paymentError,
        paymentIntent: currentState.paymentIntent,
      });

      this.emit({
        type: 'payment_failed',
        payload: {
          error: paymentError,
          paymentIntent: currentState.paymentIntent,
        },
      });

      throw error;
    }
  }

  /**
   * Resume polling after 3D Secure or QR code flow completes
   *
   * This method should be called after the user completes 3D Secure authentication
   * or scans a QR code. It resumes payment status polling while ignoring subsequent
   * action URLs or QR codes to prevent showing the same modal/action multiple times.
   *
   * The SDK will continue polling until it receives a terminal status (succeeded or failed).
   *
   * @example
   * ```typescript
   * // After 3DS authentication completes
   * sdk.on('payment_requires_action', (event) => {
   *   const authWindow = window.open(event.payload.actionUrl);
   *
   *   // Monitor when auth window closes
   *   const checkClosed = setInterval(() => {
   *     if (authWindow?.closed) {
   *       clearInterval(checkClosed);
   *       sdk.resumePolling(); // Resume polling for final status
   *     }
   *   }, 500);
   * });
   *
   * // After QR code is scanned
   * sdk.on('qr_code_received', (event) => {
   *   displayQRCode(event.payload.qrCode);
   *   // User scans QR code with their banking app
   *   // Once they confirm payment in their app:
   *   sdk.resumePolling(); // Resume polling for final status
   * });
   * ```
   *
   * @public
   */
  public resumePolling(): void {
    if (!this.paymentToken) {
      return;
    }

    // Set flags to ignore nextActionUrl and qrCode in subsequent polling responses
    // After 3DS completes or QR is scanned, we only want to watch for final status
    this.ignore3DSInPolling = true;
    this.ignoreQRInPolling = true;

    // Restart polling with both ignore flags set to true
    this.startPolling();
  }

  /**
   * Destroy the SDK instance and clean up all resources
   *
   * This method should be called when you're done with the SDK instance to:
   * - Stop all polling operations
   * - Remove all event listeners
   * - Clear all state subscribers
   * - Free up memory
   *
   * After calling destroy(), the SDK instance should not be used again.
   *
   * @example
   * ```typescript
   * // When component unmounts or page unloads
   * useEffect(() => {
   *   const sdk = new PaymentSDK(config, adapters);
   *
   *   return () => {
   *     sdk.destroy(); // Cleanup on unmount
   *   };
   * }, []);
   * ```
   *
   * @public
   */
  public destroy(): void {
    this.stopPolling();
    this.subscribers.clear();
    Object.keys(this.eventListeners).forEach((key) => {
      delete this.eventListeners[key as keyof EventListenerMap];
    });
  }

  /**
   * Start polling for payment status
   */
  private startPolling(): void {
    if (!this.paymentToken) {
      return;
    }

    // Stop existing polling if any
    this.stopPolling();

    // Create and start new polling service
    this.pollingService = new PollingService(
      this.adapters,
      this.getApiBaseUrl(),
      this.paymentToken,
      this.handlePollingUpdate.bind(this),
      {
        interval: 4000, // Poll every 4 seconds
        maxAttempts: 100, // Max 100 attempts = 6.67 minutes
        timeout: 400000, // 400 second timeout (~6.67 minutes)
        ignore3DS: this.ignore3DSInPolling, // Pass flag to polling service
        ignoreQR: this.ignoreQRInPolling, // Pass flag to polling service
      },
      this.handlePollStart.bind(this), // Pass poll start callback
    );

    this.pollingService.start();
  }

  /**
   * Stop polling service
   */
  private stopPolling(): void {
    if (this.pollingService) {
      this.pollingService.stop();
      this.pollingService = null;
    }
  }

  /**
   * Handle poll start - called before each getPaymentData request
   */
  private handlePollStart(): void {
    // Emit event to notify that getPaymentData is about to be invoked
    // This is useful for React Native where we need to close 3DS modal when polling resumes
    this.emit({
      type: 'poll_started',
      payload: {},
    });
  }

  /**
   * Handle polling status update
   */
  private handlePollingUpdate(response: PaymentStatusResponse): void {
    if (response.error) {
      // Polling error occurred, but continue current state
      return;
    }

    // Get current state
    const currentState = this.state;

    if (currentState.status !== 'processing' && currentState.status !== 'requires_action') {
      // No longer in a pollable state, stop polling
      this.stopPolling();
      return;
    }

    // Priority 1: Check for QR code image (if backend provides it separately)
    // BUT: Skip this check if we're resuming after QR scan (ignoreQRInPolling flag is true)
    // ALSO: Skip for redirect-based payment methods that use nextActionUrl instead of QR display
    const isRedirectBasedMethod =
      currentState.selectedMethod === 'promptpay' ||
      currentState.selectedMethod === 'duitnow' ||
      currentState.selectedMethod === 'gopay' ||
      currentState.selectedMethod === 'boost' ||
      currentState.selectedMethod === 'shopeepay' ||
      currentState.selectedMethod === 'atom' ||
      currentState.selectedMethod === 'dana' ||
      currentState.selectedMethod === 'tng' ||
      currentState.selectedMethod === 'alipaycn' ||
      currentState.selectedMethod === 'alipayhk' ||
      currentState.selectedMethod === 'gcash' ||
      currentState.selectedMethod === 'konbini' ||
      currentState.selectedMethod === 'payeasy' ||
      currentState.selectedMethod === 'grabpay_sg' ||
      currentState.selectedMethod === 'fpx';

    if (response.qrCode && !this.ignoreQRInPolling && !isRedirectBasedMethod) {
      this.emit({
        type: 'qr_code_received',
        payload: {
          qrCode: response.qrCode,
          methodId: currentState.selectedMethod!,
        },
      });
      return;
    }

    // Priority 2: Check for nextActionUrl
    // BUT: Skip this check if we're resuming after action (ignore3DSInPolling/ignoreQRInPolling)
    if (response.nextActionUrl && !this.ignore3DSInPolling) {
      // Check if this is Prompt Pay - emit redirect event for external payment page
      if (currentState.selectedMethod === 'promptpay') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        // The core SDK is platform-agnostic, so redirect is handled by the consumer
        this.emit({
          type: 'promptpay_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is DuitNow - emit redirect event for external payment page
      if (currentState.selectedMethod === 'duitnow') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        // The core SDK is platform-agnostic, so redirect is handled by the consumer
        this.emit({
          type: 'duitnow_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is GoPay - emit redirect event for external payment page
      if (currentState.selectedMethod === 'gopay') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        this.emit({
          type: 'gopay_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is Boost - emit redirect event for external payment page
      if (currentState.selectedMethod === 'boost') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        this.emit({
          type: 'boost_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is ShopeePay - emit redirect event for external payment page
      if (currentState.selectedMethod === 'shopeepay') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        this.emit({
          type: 'shopeepay_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is Atom - emit redirect event for external payment page
      if (currentState.selectedMethod === 'atom') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        this.emit({
          type: 'atom_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is Dana - emit redirect event for external payment page
      if (currentState.selectedMethod === 'dana') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        this.emit({
          type: 'dana_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is Touch 'n Go - emit redirect event for external payment page
      if (currentState.selectedMethod === 'tng') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        this.emit({
          type: 'tng_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is Alipay CN - emit redirect event for external payment page
      if (currentState.selectedMethod === 'alipaycn') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        this.emit({
          type: 'alipaycn_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is Alipay HK - emit redirect event for external payment page
      if (currentState.selectedMethod === 'alipayhk') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        this.emit({
          type: 'alipayhk_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is GCash - emit redirect event for external payment page
      if (currentState.selectedMethod === 'gcash') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        this.emit({
          type: 'gcash_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is Konbini - emit redirect event for convenience store payment page
      if (currentState.selectedMethod === 'konbini') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        this.emit({
          type: 'konbini_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is Pay-Easy - emit redirect event for bank transfer/ATM payment page
      if (currentState.selectedMethod === 'payeasy') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        this.emit({
          type: 'payeasy_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is GrabPay SG - emit redirect event for external payment page
      if (currentState.selectedMethod === 'grabpay_sg') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        this.emit({
          type: 'grabpay_sg_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is FPX - emit redirect event for bank transfer page
      if (currentState.selectedMethod === 'fpx') {
        // Stop polling - we're leaving the page
        this.stopPolling();
        // Emit event for web component to handle redirect
        this.emit({
          type: 'fpx_redirect',
          payload: {
            redirectUrl: response.nextActionUrl,
            methodId: currentState.selectedMethod,
          },
        });
        return;
      }

      // Check if this is PayNow payment - treat nextActionUrl as QR flow
      if (currentState.selectedMethod === 'paynow' && !this.ignoreQRInPolling) {
        this.emit({
          type: 'qr_code_received',
          payload: {
            qrCode: response.nextActionUrl, // Pass URL to show in iframe
            methodId: currentState.selectedMethod!,
          },
        });
        return;
      }

      // For card/other methods, treat as 3DS
      const updatedIntent: PaymentIntent = {
        id: response.paymentIntentId,
        amount: response.amount ?? currentState.paymentIntent!.amount,
        currency: response.currency ?? currentState.paymentIntent!.currency,
        status: PAYMENT_STATUS.REQUIRES_ACTION,
        ...(response.metadata && {
          metadata: response.metadata as Record<string, string | number | boolean>,
        }),
      };

      this.setState({
        status: PAYMENT_STATUS.REQUIRES_ACTION,
        paymentIntent: updatedIntent,
        actionUrl: response.nextActionUrl,
        selectedMethod: currentState.selectedMethod,
        availableMethods: currentState.availableMethods,
      });

      this.emit({
        type: 'payment_requires_action',
        payload: {
          paymentIntent: updatedIntent,
          actionUrl: response.nextActionUrl,
          methodId: currentState.selectedMethod!,
        },
      });
      return;
    }

    // Update payment intent with latest status
    const updatedIntent: PaymentIntent = {
      id: response.paymentIntentId,
      amount: response.amount ?? currentState.paymentIntent!.amount,
      currency: response.currency ?? currentState.paymentIntent!.currency,
      status: response.status,
      ...(response.metadata && {
        metadata: response.metadata as Record<string, string | number | boolean>,
      }),
    };

    // Priority 3: Handle terminal statuses
    if (response.status === 'succeeded') {
      this.stopPolling();
      this.setState({
        status: PAYMENT_STATUS.SUCCEEDED,
        paymentIntent: updatedIntent,
        selectedMethod: currentState.selectedMethod,
      });

      this.emit({
        type: 'payment_succeeded',
        payload: {
          paymentIntent: updatedIntent,
          methodId: currentState.selectedMethod!,
        },
      });
    } else if (response.status === 'failed') {
      this.stopPolling();
      const errorMessage = response.error
        ? typeof response.error === 'object' &&
          response.error !== null &&
          'message' in response.error
          ? String((response.error as { message: unknown }).message)
          : 'Payment failed'
        : 'Payment failed';
      const paymentError = {
        code: 'PAYMENT_FAILED',
        message: errorMessage,
        timestamp: this.adapters.timer.now(),
      };

      this.setState({
        status: PAYMENT_STATUS.FAILED,
        error: paymentError,
        paymentIntent: updatedIntent,
      });

      this.emit({
        type: 'payment_failed',
        payload: {
          error: paymentError,
          paymentIntent: updatedIntent,
        },
      });
    }
    // If still processing/initializing, continue polling (service handles this automatically)
  }

  /**
   * Set state and notify subscribers
   */
  private setState(newState: State): void {
    const previousStatus = this.state.status;
    this.state = newState;

    // Notify all subscribers
    this.subscribers.forEach((subscriber) => {
      subscriber(this.state);
    });

    // Emit state changed event
    if (previousStatus !== newState.status) {
      this.emit({
        type: 'state_changed',
        payload: {
          previousStatus,
          currentStatus: newState.status,
        },
      });
    }
  }

  /**
   * Emit event to listeners
   */
  private emit<E extends Event>(event: E): void {
    const listeners = this.eventListeners[event.type];
    if (listeners !== undefined) {
      listeners.forEach((listener) => {
        // Type assertion needed here
        (listener as EventListener<E>)(event);
      });
    }
  }
}
